import type { ReactNode } from 'react';
import { StyleSheet, View, type StyleProp, type ViewStyle } from 'react-native';

import { colors, radii, shadows, spacing } from '@/constants/theme';

type Variant = 'default' | 'dark' | 'flat';

export interface CardProps {
  children: ReactNode;
  variant?: Variant;
  /** Override default padding (`spacing.base`). Use 0 to inset content yourself. */
  padding?: number;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}

/**
 * Container with the brand-canonical 20px radius (handoff app.css `mp-card`).
 * Three variants: default (light surface + border + soft shadow), dark
 * (Deep Green + Frost text, bigger shadow), flat (no shadow). The dark
 * variant is the hero pattern used on home/cashout balance cards.
 */
export function Card({ children, variant = 'default', padding, style, testID }: CardProps) {
  return (
    <View
      testID={testID}
      style={[
        styles.base,
        VARIANTS[variant],
        padding !== undefined && { padding },
        style,
      ]}
    >
      {children}
    </View>
  );
}

const VARIANTS: Record<Variant, ViewStyle> = {
  default: {
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    ...shadows.card,
  },
  dark: {
    backgroundColor: colors.deepGreen,
    ...shadows.modal,
  },
  flat: {
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
  },
};

const styles = StyleSheet.create({
  base: {
    borderRadius: radii.lg,
    padding: spacing.base,
  },
});
